import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { useRouter } from 'next/navigation';
import { faArrowLeft, faPlus, faTimes } from '@fortawesome/pro-light-svg-icons';

import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  ProgressiveSearchModalKeys,
  SelectedProcedures,
} from '@/app/(main)/_components/search-modal/search-modal.types';
import { appendRecentSearches, useSearchState } from '@/store/search/search.store';
import { useRecentSearchesStorage } from '@/storage/recents-searches.storage';
import { isVaccineSearchMode } from '@/utils/search/search-vaccine.util';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { useLoading } from '@/hooks/use-loading.hook';
import { buildSearchResult } from '@/utils/search/search-result.util';
import { useSearch } from '@/app/_hooks/search/use-search.hook';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { getLogger } from '@/logger/logger';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';

interface AskAddMoreExamsProps {
  selectedProcedures: SelectedProcedures[];
  locationOverride?: OverridedUserLocation;
}

export function AskAddMoreExams({ selectedProcedures, locationOverride }: AskAddMoreExamsProps) {
  const [, setStoredRecentSearches] = useRecentSearchesStorage();

  const { handleJump } = useProgressiveModal<ProgressiveSearchModalKeys>();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const { handleSearchExamsUrl } = useSearch({ locationOverride });
  const analytics = getAnalyticsPlugin();

  function handleBack() {
    if (selectedProcedures.length === 0) {
      handleJump({ key: ProgressiveSearchModalKeys.SEARCH, returning: true });
      return;
    }
    handleJump({ key: ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE, returning: true });
  }

  const isVaccine = isVaccineSearchMode(useSearchState.getState().selectedSearch);
  const modalTitle = isVaccine ? 'Você adicionou uma vacina' : 'Você adicionou um exame laboratorial';
  const modalDescription = isVaccine
    ? 'Deseja acrescentar mais vacinas antes de ver os valores?'
    : 'Deseja acrescentar mais exames antes de ver os valores?';
  const addMoreProceduresTextButton = isVaccine ? 'Adicionar mais vacinas' : 'Adicionar mais exames';
  const keepOnlyProcedureTextButton = isVaccine ? 'Apenas esta vacina' : 'Apenas este exame';

  const router = useRouter();
  const handleNavigateToScheduleResult = async (e: React.MouseEvent): Promise<void> => {
    analytics.track(AnalyticsEnum.VIEW_SEARCH_ADD_MORE_EXAMES_OR_KEEP_EXAMS, {
      decision: 'keep_only_this_exam',
    });
    startLoading();
    e.preventDefault();

    try {
      const searchResult = buildSearchResult(selectedProcedures, isVaccine);

      if (searchResult) {
        appendRecentSearches(searchResult, setStoredRecentSearches);
      }

      router.push(handleSearchExamsUrl(isVaccine, selectedProcedures));
    } catch (error) {
      const logger = getLogger('SearchModal - AskAddMoreExams');
      logger.error('Error updating latest searches', { selectedProcedures, error });
    } finally {
      stopLoading();
    }
  };

  function jumpSelectMoreExams() {
    analytics.track(AnalyticsEnum.VIEW_SEARCH_ADD_MORE_EXAMES_OR_KEEP_EXAMS, {
      decision: 'add_more_exams',
    });
    handleJump({ key: ProgressiveSearchModalKeys.SELECT_MORE_EXAMS });
  }

  return (
    <div
      className="h-full flex flex-col justify-between flex-1 py-5 px-6"
      data-testid={DataTestIdEnum.PROCEDURES_MODAL_ASK_ADD_MORE_EXAMS}
    >
      <SheetHeader>
        <div className="flex justify-between gap-2.5 items-start md:items-center">
          <div className="flex gap-4 items-start md:items-center">
            <button
              data-testid="back--ask-add-more-exams"
              onClick={handleBack}
            >
              <Icon
                icon={faArrowLeft}
                size="xl"
                className="text-gray-500 self-center cursor-pointer"
              />
            </button>
            <SheetTitle>
              <div className="flex gap-3">
                <Typography
                  variant={TypographyVariant.MODAL_TITLE}
                  className="text-wrap text-left"
                >
                  {modalTitle}
                </Typography>
              </div>
            </SheetTitle>
          </div>
          <button
            className="text-gray-500 cursor-pointer self-start md:self-center mt-2 md:mt-0 hover:bg-gray-50"
            data-testid="close--ask-add-more-exams"
            onClick={handleBack}
          >
            <Icon icon={faTimes} size="xl" className="hover:text-gray-900 text-[1.625rem]" />
          </button>
        </div>
      </SheetHeader>

      <div className="flex flex-col justify-between h-full">
        <Typography className="my-4">{modalDescription}</Typography>

        <div className="flex flex-col gap-4">
          <Button
            data-testid="add-more-exams--ask-add-more-exams"
            disabled={isLoading}
            startIcon={faPlus}
            onClick={() => jumpSelectMoreExams()}
          >
            {addMoreProceduresTextButton}
          </Button>
          <Button
            variant="secondary"
            data-testid="keep-only-this-exam--ask-add-more-exams"
            isLoading={isLoading}
            onClick={handleNavigateToScheduleResult}
          >
            {keepOnlyProcedureTextButton}
          </Button>
        </div>
      </div>
    </div>
  );
}
